import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAudit, downloadAuditPdf } from '../services/api';

const severityConfig = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800', icon: '!', iconBg: 'bg-red-500' },
  warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800', icon: '!', iconBg: 'bg-yellow-500' },
  informational: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800', icon: 'i', iconBg: 'bg-blue-500' },
};

export default function AuditDetail() {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFindings, setExpandedFindings] = useState({});
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const res = await getAudit(id);
        setAudit(res.data.audit);
        setFindings(res.data.findings || []);
      } catch (err) {
        console.error('Failed to fetch audit:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
    // Poll if processing
    const interval = setInterval(() => {
      if (audit?.status === 'processing') fetchAudit();
    }, 5000);
    return () => clearInterval(interval);
  }, [id, audit?.status]);

  const toggleFinding = (findingId) => {
    setExpandedFindings(prev => ({ ...prev, [findingId]: !prev[findingId] }));
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await downloadAuditPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ChartAudit-Report-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download PDF');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Loading audit...</p></div>;
  }
  if (!audit) {
    return <div className="max-w-4xl mx-auto px-4 py-8"><p className="text-red-600">Audit not found</p></div>;
  }

  const filteredFindings = filterSeverity === 'all' ? findings : findings.filter(f => f.severity === filterSeverity);
  const scoreColor = audit.compliance_score >= 80 ? 'text-green-600' : audit.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link to="/audits" className="text-blue-600 hover:underline text-sm">&larr; Back to Audits</Link>
          <h1 className="text-2xl font-bold text-slate-800 mt-2">Audit Report</h1>
          <p className="text-gray-500">{audit.original_filename} — {new Date(audit.created_at).toLocaleDateString()}</p>
          {audit.patient_full_name && <p className="text-slate-700 font-medium">Patient: {audit.patient_full_name}</p>}
          {audit.client_agency_name && <p className="text-gray-500">Agency: {audit.client_agency_name}</p>}
          {audit.patient_reference && <p className="text-gray-400 text-sm">Chart Ref: {audit.patient_reference}</p>}
        </div>
        {audit.status === 'completed' && (
          <button onClick={handleDownloadPdf}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm">
            Download PDF Report
          </button>
        )}
      </div>

      {/* Processing state */}
      {audit.status === 'processing' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
          <div className="text-4xl mb-4 animate-pulse">&#129302;</div>
          <p className="text-xl font-semibold text-blue-800">AI Audit in Progress</p>
          <p className="text-blue-600 mt-2">Claude is analyzing your document against CMS standards...</p>
          <p className="text-sm text-blue-400 mt-4">This page will auto-refresh when complete</p>
        </div>
      )}

      {audit.status === 'failed' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <p className="text-xl font-semibold text-red-800">Audit Failed</p>
          <p className="text-red-600 mt-2">{audit.error_message || 'An unexpected error occurred'}</p>
        </div>
      )}

      {audit.status === 'completed' && (
        <>
          {/* Score & Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <p className="text-sm text-gray-500">Compliance Score</p>
              <p className={`text-5xl font-bold mt-1 ${scoreColor}`}>{audit.compliance_score}</p>
              <p className="text-gray-400 text-xs mt-1">out of 100</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <p className="text-sm text-gray-500">Critical</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{audit.critical_count}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <p className="text-sm text-gray-500">Warnings</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{audit.warning_count}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <p className="text-sm text-gray-500">Informational</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{audit.info_count}</p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex space-x-2 mb-4">
            {['all', 'critical', 'warning', 'informational'].map(sev => (
              <button key={sev} onClick={() => setFilterSeverity(sev)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  filterSeverity === sev ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {sev === 'all' ? `All (${findings.length})` :
                  `${sev.charAt(0).toUpperCase() + sev.slice(1)} (${findings.filter(f => f.severity === sev).length})`}
              </button>
            ))}
          </div>

          {/* Findings List */}
          <div className="space-y-3">
            {filteredFindings.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                <p className="text-green-800 font-semibold">No findings in this category</p>
              </div>
            ) : (
              filteredFindings.map((finding) => {
                const config = severityConfig[finding.severity];
                const isExpanded = expandedFindings[finding.id];
                return (
                  <div key={finding.id}
                    className={`${config.bg} border ${config.border} rounded-xl overflow-hidden`}>
                    <button onClick={() => toggleFinding(finding.id)}
                      className="w-full flex items-center justify-between p-4 text-left">
                      <div className="flex items-center space-x-3">
                        <span className={`${config.iconBg} text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold`}>
                          {config.icon}
                        </span>
                        <div>
                          <span className={`${config.badge} text-xs font-semibold px-2 py-0.5 rounded-full`}>
                            {finding.severity.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500 ml-2">{finding.category}</span>
                          <p className="font-semibold text-slate-800 mt-1">{finding.title}</p>
                        </div>
                      </div>
                      <span className="text-gray-400 text-lg">{isExpanded ? '−' : '+'}</span>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pl-14 space-y-3">
                        {/* Source location tags */}
                        {(finding.page_numbers || finding.note_type || finding.oasis_field) && (
                          <div className="flex flex-wrap gap-2">
                            {finding.page_numbers && (
                              <span className="bg-slate-200 text-slate-700 text-xs font-semibold px-2 py-1 rounded">
                                {finding.page_numbers}
                              </span>
                            )}
                            {finding.note_type && (
                              <span className="bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded">
                                {finding.note_type}
                              </span>
                            )}
                            {finding.oasis_field && (
                              <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded">
                                OASIS: {finding.oasis_field}
                              </span>
                            )}
                          </div>
                        )}

                        <p className="text-slate-700 text-sm">{finding.description}</p>

                        {/* Direct quote from document */}
                        {finding.direct_quote && (
                          <div className="border-l-4 border-slate-300 bg-white bg-opacity-70 pl-3 py-2">
                            <p className="text-xs text-gray-500 font-medium mb-1">From Document:</p>
                            <p className="text-sm text-slate-600 italic">"{finding.direct_quote}"</p>
                          </div>
                        )}

                        {finding.recommendation && (
                          <div className="bg-white bg-opacity-60 rounded-lg p-3">
                            <p className="text-sm font-semibold text-green-800">Recommendation:</p>
                            <p className="text-sm text-slate-700 mt-1">{finding.recommendation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Email Status */}
          {audit.email_sent && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
              PDF report was emailed to your agency on {new Date(audit.email_sent_at).toLocaleString()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
